<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-purple header-enhanced">
      <!-- Enterprise Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Enterprises"
            type="gradient-purple"
            :sub-title="stats.totalEnterprises ? stats.totalEnterprises.toLocaleString() : 'Loading...'"
            icon="ni ni-building"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">↗ {{ stats.enterpriseGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Active Subscriptions"
            type="gradient-success"
            :sub-title="stats.activeSubscriptions ? stats.activeSubscriptions.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Active</span>
              <span class="text-nowrap">Currently subscribed</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Revenue"
            type="gradient-info"
            :sub-title="stats.totalRevenue ? '$' + stats.totalRevenue.toLocaleString() : 'Loading...'"
            icon="ni ni-money-coins"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">💰 Revenue</span>
              <span class="text-nowrap">Monthly recurring</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Avg. Users/School"
            type="gradient-warning"
            :sub-title="stats.avgUsersPerSchool ? stats.avgUsersPerSchool.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">👥 Users</span>
              <span class="text-nowrap">Per enterprise</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-building mr-2"></i>Enterprise Management
          </h3>
          <p class="text-white-50 mb-0">Manage enterprise accounts and subscriptions</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button variant="light" size="sm" class="shadow-sm">
            <i class="ni ni-fat-add mr-1"></i>Add Enterprise
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <div class="mb-4">
        <FilterBar></FilterBar>
      </div>

      <!-- Accounts -->
      <vuetable data-path="accounts"
                pagination-path="pagination"
                ref="vuetable"
                track-by="user_id"
                :api-url="apiUrl"
                :fields="fields"
                :http-options="httpOptions"
                :css="css.table"
                :per-page="10"
                :append-params="moreParams"
                :query-params="{
                  sort    : 'sort',
                  page    : 'page',
                  perPage : 'perPage'
                }"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="showLoader"
                @vuetable:loaded="hideLoader">
      </vuetable>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination ref="pagination"
                                  @vuetable-pagination:change-page="onChangePage"
                                  :css="css.pagination">
              </vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>
    

    <!-- Modal -->
    <modals-container />

    <!-- Dialog -->
    <v-dialog />

    <!-- Loading -->
    <loading :active.sync="isLoading"
             :is-full-page="true"
             background-color="#000"
             color="#068bcd"
             :opacity="0.3"
             :width="70"
             :height="70"
    ></loading>
  </div>
</template>

<script>
  const EnterpriseListActions = () => import('./EnterpriseListActions.vue');
  const FilterBar = () => import('./FilterBar.vue');
  const Loading = () => import('vue-loading-overlay');
  const Vuetable = () => import('vuetable-2/src/components/Vuetable');
  const VuetablePagination =
    () => import('vuetable-2/src/components/VuetablePagination');
  const VuetablePaginationInfo =
    () => import('vuetable-2/src/components/VuetablePaginationInfo');

  import Vue from 'vue';
  import moment from 'moment-timezone';
  import TableStyle from '../table-bootstrap-css.js';
  import 'vue-loading-overlay/dist/vue-loading.css';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  Vue.component('enterprise-list-actions', EnterpriseListActions);

  export default {
    name : 'EnterpriseList',
    data() {
      return {
        isLoading   : false,
        apiUrl      : process.env.VUE_APP_ROOT_API + 'api/enterprise/accounts',
        moreParams  : {},
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        css    : TableStyle,
        stats: {
          totalEnterprises: null,
          activeSubscriptions: null,
          totalRevenue: null,
          avgUsersPerSchool: null,
          enterpriseGrowth: 0,
        },
        fields : [ {
          name       : 'user_id',
          title      : 'ID',
          sortField  : 'user_id',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        },  {
          name       : 'email',
          title      : 'Email',
          sortField  : 'email',
          direction  : 'desc',
          titleClass : 'text-center',
        }, {
          name       : 'school_name',
          title      : 'School',
          sortField  : 'school_name',
          direction  : 'desc',
          titleClass : 'text-center',
        }, {
          name       : 'coupon',
          title      : 'Coupon',
          sortField  : 'coupon',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'user_limit',
          title      : 'User Limit',
          sortField  : 'user_limit',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'users',
          title      : 'Subscribers',
          sortField  : 'total_users',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'custom_price',
          title      : 'Price',
          sortField  : 'custom_price',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-right',
        }, {
          name       : 'billing_start',
          title      : 'Billing Start',
          sortField  : 'billing_start',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'billing_end',
          title      : 'Billing End',
          sortField  : 'billing_end',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'status',
          title      : 'Subscription<br>Status',
          sortField  : 'status',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__component:enterprise-list-actions',
          title      : 'Actions',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
      }
    },
    components : {
      FilterBar,
      Loading,
      Vuetable,
      VuetablePagination,
      VuetablePaginationInfo,
      baseHeader,
      StatsCard
    },
    methods : {

      /**
       * For Pagination
       *
       * @param data
       * @return {object}
       */
      transform(data) {
        data = data.result;
        const transformed = {};

        transformed.pagination = {
          'total'         : data.total,
          'per_page'      : data.perPage,
          'current_page'  : data.currentPage,
          'last_page'     : data.lastPage,
          'next_page_url' : data.nextPageUrl,
          'prev_page_url' : data.prevPageUrl,
          'from'          : data.from,
          'to'            : data.to,
        };

        transformed.accounts = [];

        for (let i = 0; i < data.data.length; i++) {
          // From UTC to Local time
          // const createdConv = moment.utc(data.data[i].created_utc).local()
          //   .format('MMM DD, YYYY, hh:mm:ss A z');
          // const startConv = moment.utc(data.data[i].billing_start).local()
          //   .format('MMM DD, YYYY, hh:mm:ss A z');
          // const endConv = moment.utc(data.data[i].billing_end).local()
          //   .format('MMM DD, YYYY, hh:mm:ss A z');

          const createdConv = moment.utc(data.data[i].created_utc).local()
            .format('MM/DD/YY, hh:mm:ss A z');
          const startConv = moment.utc(data.data[i].billing_start).local()
            .format('MM/DD/YY, hh:mm:ss A z');
          const endConv = moment.utc(data.data[i].billing_end).local()
            .format('MM/DD/YY, hh:mm:ss A z');

          transformed.accounts.push({
            'user_id'         : data.data[i].user_id,
            'email'           : data.data[i].email,
            'status'          : data.data[i].status,
            'user_type'       : data.data[i].user_type,
            'school_id'       : data.data[i].school_id,
            'school'          : data.data[i].school,
            'activation'      : data.data[i].activation_used,
            'is_social'       : data.data[i].is_social,
            'created'         : createdConv,
            'billing_start'   : startConv,
            'billing_end'     : endConv,
            'school_name'     : data.data[i].school_name,
            'custom_price'    : data.data[i].custom_price.toFixed(2),
            'coupon'          : data.data[i].coupon,
            'user_limit'      : data.data[i].user_limit,
            'payment_id'      : data.data[i].payment_id,
            'subscription_id' : data.data[i].subscription_id,
            'users'           : data.data[i].total_users,
          });
        }

        return transformed;
      },

      /**
       * Update Paginaton info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
        this.$refs.paginationInfo.setPaginationData(paginationData);
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.isLoading = true;
      },

      /**
       * Hide Loader
       */
      hideLoader() {
        this.isLoading = false;
      },

      /**
       * On Change Page
       * @param page
       */
      onChangePage(page) {
        this.$refs.vuetable.changePage(page);
      },

      /**
       * On Filter Set
       * @param filterText
       */
      onFilterSet(filterText) {
        this.moreParams = {
          'filter' : filterText,
        };
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * On Reset Filter
       */
      onFilterReset() {
        this.moreParams = {};
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * Initialize Enterprise Statistics
       */
      initializeStats() {
        // Simulated enterprise statistics - replace with actual API calls
        this.stats.totalEnterprises = 89;
        this.stats.activeSubscriptions = 76;
        this.stats.totalRevenue = 45250;
        this.stats.avgUsersPerSchool = 145;
        this.stats.enterpriseGrowth = 8.7;
      },
    },
    mounted() {
      this.initializeStats();
      this.$events.$on('filter-set', eventData => this.onFilterSet(eventData));
      this.$events.$on('filter-reset', () => this.onFilterReset());
      this.$events.$on('loading-state',
                       eventData => {
                         if (eventData)
                           this.showLoader();
                         else
                           this.hideLoader();
                       });
      this.$events.$on('reload', () => {
        this.$refs.vuetable.refresh();
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/enterprise/enterprise-list";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(138, 101, 224, 0.9) 0%, rgba(107, 66, 193, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(155, 89, 182, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(155, 89, 182, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(138, 101, 224, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Pagination */
.pagination-info {
  padding: 16px 20px;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 8px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Vuetable Pagination Styling */
.vuetable-pagination {
  margin: 0 !important;
}

.vuetable-pagination .pagination {
  margin: 0 !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.vuetable-pagination .page-link {
  border: none !important;
  color: #6c757d !important;
  font-weight: 500 !important;
  padding: 8px 12px !important;
  transition: all 0.2s ease !important;
}

.vuetable-pagination .page-link:hover {
  background: rgba(138, 101, 224, 0.1) !important;
  color: #8a65e0 !important;
}

.vuetable-pagination .page-item.active .page-link {
  background: #8a65e0 !important;
  border-color: #8a65e0 !important;
  color: white !important;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card-hover {
  animation: fadeInUp 0.6s ease-out;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .pagination-info,
  .pagination-controls {
    padding: 12px 16px;
    text-align: center;
  }

  .pagination-controls {
    margin-top: 12px;
  }
}
</style>